<template>
  <main class="app-shell">
    <!-- Shared Background Layer -->
    <div class="bg-base"></div>
    <div class="bg-grid"></div>
    <div class="bg-blob bg-blob-1"></div>
    <div class="bg-blob bg-blob-2"></div>
    <div class="bg-blob bg-blob-3"></div>
    <div class="bg-border-overlay"></div>

    <div class="cockpit-topbar">
      <el-button class="cockpit-back-btn" size="mini" type="primary" icon="el-icon-back" plain @click="goBack">返回</el-button>
    </div>

    <div class="main-grid">
      <!-- ===== LEFT COLUMN (3fr) ===== -->
      <div class="col">
        <!-- Card 1: User Identity -->
        <div class="glass-card card-identity">
          <div class="avatar">{{user.nickname}}</div>
          <div class="identity-info">
            <div class="identity-name">{{user.name}}能力画像</div>
            <div class="identity-tags">
              <span class="id-tag id-tag-cyan">{{levels.exam?.label}}</span>
              <span class="id-tag id-tag-gold">{{levels.read?.label}}</span>
              <!-- <span class="id-level-badge">↑ 较上周提升1级</span> -->
            </div>
          </div>
        </div>
 
        <!-- Card 2: Average Accuracy -->
        <div class="glass-card card-accuracy">
          <div class="acc-label">平均正确率</div>
          <div class="acc-main">
            <span class="acc-value glow-cyan">{{accuracy.value}}<span class="acc-unit">%</span></span>
            <div class="acc-change">
              <div class="acc-change-val">{{weekChange.trend == 'up' ? '↑' : '↓'}} {{weekChange.value}}%</div>
              <div class="acc-change-desc">周变化</div>
            </div>
          </div>
          <div class="acc-bar-track">
            <div class="acc-bar-fill" :style="{ width: accuracy.value + '%' }"></div>
          </div>
          <div class="acc-rank-row">
            <span class="rank-pill">{{ranking.displayText}}</span>
            <span class="rank-school">全校 {{ranking.rank}}</span>
          </div>
          <!-- <div class="sparkline-wrap">
            <div ref="sparkline" class="sparkline-canvas"></div>
          </div> -->
          <div style="width: 100%; height: 200px;">
              <div id="weekChart" style="width: 100%; height: 100%;"></div>
          </div>
          <div class="ranking-separator"></div>
          <div class="rank-detail-row">
            <i class="el-icon-trophy rank-trophy"></i>
            <span class="rank-detail-text">
              超越<span class="rank-num-gold"> {{ranking.percentile}}% </span>学习者 · 全校
              <span class="rank-num-gold"> {{ranking.rank}} </span>
            </span>
          </div>
          <div class="rank-bar-track">
            <div class="rank-bar-fill" :style="{ width: ranking.progress + '%' }"></div>
          </div>
          <div class="rank-footnote">基于最近7天综合表现，每日更新</div>
        </div>
 
        <!-- Card 3: Consolidation Alerts -->
        <div class="glass-card card-alerts">
          <div class="alerts-header">
            <div class="pulse-dot pulse-dot-red"></div>
            <span class="alerts-title">待巩固题型</span>
          </div>
          <div class="alerts-list">
            <div
              v-for="a in consolidationAlerts"
              :key="a.typeId"
              class="consolidation-alert"
              :class="{ 'alert-highlight': isAlertHighlighted(a) }"
              :data-alert-name="a.fullName"
              :data-alert-typename="extractTypeName(a.fullName)"
            >
              <div class="pulse-dot pulse-dot-red alert-dot"></div>
              <span class="alert-name">{{ a.fullName }} {{ a.accuracy }}%</span>
              <button
                v-if="!isInPlan(a.fullName)"
                class="ca-btn ca-btn-active"
                @click.stop="addAlertToPlan(a)"
              >
                立即巩固
              </button>
              <button v-else class="ca-btn ca-btn-locked" disabled>✅已加入</button>
            </div>
          </div>
        </div>
      </div>
 
      <!-- ===== CENTER COLUMN (5fr) ===== -->
      <div class="col center-col">
        <!-- Card 4: Radar Chart -->
        <div class="glass-card card-radar">
          <div class="card-header">
            <div class="card-title-group">
              <i class="el-icon-data-line card-icon"></i>
              <span class="card-title">四维能力雷达</span>
            </div>
            <div class="period-btns">
              <button
                v-for="p in periods"
                :key="p.value"
                class="period-btn"
                :class="activePeriod === p.value ? 'period-active' : 'period-inactive'"
                @click="switchPeriod(p.value)"
              >
                {{ p.label }}
              </button>
            </div>
          </div>
          <!-- <div class="radar-wrap">
            <div ref="radar" class="radar-canvas"></div>
          </div> -->
          <div style="width: 100%; height: 100%;">
              <div id="radarChart" style="width: 100%; height: 100%;"></div>
          </div>

        </div>
 
        <!-- Card 5: Root Cause Analysis -->
        <div class="glass-card card-weakness">
          <div class="weakness-summary">{{ weaknessSummaryText }}</div>
          <div class="card-header weakness-header">
            <div class="card-title-group">
              <i class="el-icon-search card-icon"></i>
              <span class="card-title">病因分析</span>
            </div>
            <div class="weakness-tabs">
              <button
                v-for="t in weaknessTabs"
                :key="t.value"
                class="weakness-tab"
                :class="activeTab === t.value ? 'tab-active' : 'tab-inactive'"
                @click="switchTab(t.value)"
              >
                {{ t.label }}
              </button>
            </div>
          </div>
          <div class="weakness-grid">
            <template v-if="currentWeaknesses.length">
              <div
                v-for="w in currentWeaknesses"
                :key="w.fullName"
                class="weakness-card wk-card"
                :class="[weaknessBorderClass(w), { 'wk-card-highlight': highlightedWeakness === w.fullName }]"
                :data-wk-fullname="w.fullName"
              >
                <div class="wk-top-row">
                  <span class="wk-name">{{ w.fullName }}</span>
                  <span class="diag-tag" :class="computeDiagnosticTag(w).cls">
                    {{ computeDiagnosticTag(w).label }}
                  </span>
                </div>
                <div class="wk-mid-row">
                  <div class="wk-accuracy" :style="{ color: getAccuracyColor(w.accuracy) }">
                    {{ w.accuracy }}%
                  </div>
                  <i
                    class="wk-trend-icon"
                    :class="w.trend === 'down' ? 'el-icon-bottom' : 'el-icon-top'"
                    :style="{ color: w.trend === 'down' ? getAccuracyColor(w.accuracy) : 'var(--state-success)' }"
                  ></i>
                </div>
                <div class="wk-impact">{{ w.impact }}</div>
                <div class="wk-smart-wrap">
                  <el-button
                    size="mini"
                    class="wk-smart-btn"
                    
                  >智能巩固</el-button>
                </div>
                <div class="wk-sub-actions">
                  <button class="wk-sub-btn">跨级回炉</button>
                  <button class="wk-sub-btn">限时挑战</button>
                </div>
                <label
                  class="plan-toggle"
                  :style="{ cursor: isInPlanAndLocked(w.fullName) ? 'not-allowed' : 'pointer' }"
                  @click.prevent="togglePlanFromWeakness(w)"
                >
                  <div
                    class="plan-checkbox"
                    :class="{ checked: isInPlan(w.fullName), locked: isInPlanAndLocked(w.fullName) }"
                  >
                    <i v-if="isInPlan(w.fullName)" class="el-icon-check plan-check-icon"></i>
                  </div>
                  <span class="plan-toggle-label">加入今日计划</span>
                  <span v-if="isInPlanAndLocked(w.fullName)" class="plan-lock-note">(底部栏取消)</span>
                </label>
              </div>
            </template>
            <div v-else class="weakness-empty">🎉 暂无弱项，继续保持！</div>
          </div>
          <div class="attribution-hint-wrap" v-if="attributionHintText">
            <div class="attribution-hint-pill">{{ attributionHintText }}</div>
          </div>
        </div>
      </div>
 
      <!-- ===== RIGHT COLUMN (3fr) ===== -->
      <div class="glass-card card-question-types">
        <div class="card-header qt-header">
          <div class="card-title-group">
            <i class="el-icon-edit card-icon"></i>
            <span class="card-title">题型能力图谱</span>
          </div>
          <!-- <span class="qt-hint">点击联动雷达</span> -->
        </div>
        <div class="qt-list custom-scroll" ref="qtList">
          <template v-for="group in groupedQuestionTypes">
            <div :key="group.material" :class="['qt-group-header', group.isFirst ? 'mt-1' : 'mt-2']">
              <div class="qt-group-bar"></div>
              <span class="qt-group-title">{{ group.material }}</span>
              <span class="qt-group-count">共 {{ group.rows.length }} 个题型</span>
            </div>
            <div
              v-for="qt in group.rows"
              :key="qt.id"
              class="qt-row-enhanced"
              :class="rowClasses(qt)"
              :data-qt-id="qt.id"
              :data-qt-name="qt.name"
              :data-qt-fullname="qt.fullName"
              :data-qt-dim="qt.dimension"
              @click="handleRowClick(qt)"
            >
              <div class="qt-row-main">
                <div class="qt-row-line1">
                  <span class="qt-name" :style="{ color: qt.accuracy >= 80 ? 'var(--text-muted)' : 'var(--text-primary)' }">
                    {{ qt.name }}
                  </span>
                  <span class="qt-status-pill" :class="getStatusInfo(qt.accuracy).cls">
                    {{ getStatusInfo(qt.accuracy).label }}
                  </span>
                  <el-tooltip v-if="qt.priorityScore >= 60" effect="dark" placement="top">
                    <span class="priority-fire">🔥</span>
                    <div slot="content" class="fire-tooltip-content">{{ qt.priorityReason }}</div>
                  </el-tooltip>
                </div>
                <div class="qt-row-line2">
                  <div class="qt-bar-10">
                    <div
                      v-for="(c, i) in 10"
                      :key="i"
                      class="qt-bar-cell"
                      :class="{ filled: i < Math.round(qt.accuracy / 10) }"
                      :style="barCellStyle(qt, i)"
                    ></div>
                  </div>
                  <span class="qt-pct" :style="{ color: pctColor(qt) }">{{ qt.accuracy }}%</span>
                  <span v-if="renderWeeklyChange(qt)" class="acc-change" :class="weeklyChangeClass(qt)">
                    {{ renderWeeklyChange(qt) }}
                  </span>
                  <span class="lv-tag" :class="'lv-' + qt.level">Lv.{{ qt.level }}</span>
                </div>
                <div class="qt-row-line3">
                  <span class="qt-meta">已做 {{ qt.practiceCount }}题</span>
                  <span class="qt-meta-sep">·</span>
                  <span class="qt-meta">{{ getLastPracticeLabel(qt.lastPracticeDays) }}</span>
                  <template v-if="renderLevelProgressIndicator(qt)">
                    <span class="qt-meta-sep">·</span>
                    <span class="lv-progress-indicator" v-html="renderLevelProgressIndicator(qt)"></span>
                  </template>
                </div>
              </div>
              <div
                class="qt-plan-check"
                :class="{ checked: isInPlan(qt.fullName), locked: isInPlanAndLocked(qt.fullName) }"
                @click.stop="togglePlanFromRow(qt)"
              >
                <i v-if="isInPlan(qt.fullName)" class="el-icon-check qt-check-icon"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
 
    <!-- Bottom Fixed Bar -->
    <div class="bottom-bar">
      <div class="bottom-title-group">
        <i class="el-icon-document card-icon"></i>
        <span class="bottom-title">今日巩固计划</span>
      </div>
      <div class="plan-items">
        <template v-if="planItemsList.length">
          <label
            v-for="fullName in planItemsList"
            :key="fullName"
            class="plan-item-label"
            @click.prevent="togglePlanItem(fullName)"
          >
            <div class="plan-checkbox-bottom">
              <i class="el-icon-check plan-check-icon"></i>
            </div>
            <span class="plan-item-text">{{ fullName }}</span>
          </label>
        </template>
        <span v-else-if="showGuidanceHint" class="plan-guidance-hint" :style="{ opacity: guidanceOpacity }">
          💡 从右侧题型列表或病因分析中勾选，将弱项加入今日计划
        </span>
      </div>
      <span class="plan-count">{{ planItemsList.length }}项待巩固</span>
      <el-button
        class="btn-generate-pack"
        :style="generateBtnStyle"
        @click="openModal"
      >{{ generateBtnText }}</el-button>
    </div>
 
    <!-- Toast Container -->
    <div class="toast-container">
      <div v-for="t in toasts" :key="t.id" class="toast-item">{{ t.message }}</div>
    </div>
 
    <!-- Modal -->
    <el-dialog
      :visible.sync="modalVisible"
      :show-close="false"
      :modal="true"
      :append-to-body="true"
      custom-class="pack-dialog"
      width="440px"
      center
    >
      <div slot="title" class="dialog-title">
        <i class="el-icon-box dialog-title-icon"></i>
        <span>确认生成巩固包</span>
      </div>
      <div class="modal-items">
        <template v-if="planItemsList.length">
          <div v-for="fullName in planItemsList" :key="fullName" class="modal-item">
            <div class="modal-item-left">
              <i class="el-icon-circle-check modal-check-icon"></i>
              <span class="modal-item-name">{{ fullName }}</span>
            </div>
            <span class="modal-item-count">{{ modalPerType }}题</span>
          </div>
        </template>
        <div v-else class="modal-empty">暂无待巩固项，请先选择题型</div>
      </div>
      <div v-if="planItemsList.length" class="modal-total">预计共 {{ planItemsList.length * modalPerType }} 题（每个题型 2-3 题）</div>
      <div slot="footer" class="dialog-footer">
        <el-button class="dialog-cancel-btn" @click="modalVisible = false">取消</el-button>
        <el-button class="dialog-confirm-btn" @click="confirmGeneratePack">确认</el-button>
      </div>
    </el-dialog>
  </main>
</template>
 
<script>
import * as echarts from 'echarts'
import Http from "../../apis/index";

export default {
  name: 'Dashboard',
  data() {
    return {
      consolidationAlerts: [],
      questionTypes: [],
      materialOrder: [],

      radarDimNames: ['词汇深度', '语法精准度', '逻辑连贯性', '表达流利度'],
      periods: [
        { value: '7', label: '7天' },
        { value: '30', label: '30天' },
        { value: '90', label: '90天' }
      ],
      weaknessTabs: [
        { value: 'absolute', label: '绝对弱项' },
        { value: 'relative', label: '相对弱项' },
        { value: 'declining', label: '趋势下滑' },
        { value: 'leverage', label: '杠杆弱项' }
      ],
      selectedTypeId: null,
      activeTab: 'absolute',
      activePeriod: '30',
      planItems: {},        // { fullName: true }
      lockedItems: {},      // { fullName: true }
      highlightedWeakness: null,
      highlightedAlertName: null,
      highlightedDimRows: false,
      modalVisible: false,
      toasts: [],
      toastId: 0,
      showGuidanceHint: true,
      guidanceOpacity: 1,
      generateBtnText: '生成专属巩计划',
      generateBtnStyle: {},
      sparklineChart: null,
      radarChart: null,
      radarBasePointRadius: [3, 3, 3, 3],
      highlightedDim: -1,
      _guidanceTimer: null,

      user:{},
      levels:{},
      accuracy:{},
      weekChange:{},
      ranking:{},
      weaknessList: [],
      weaknessSummary: null,
      weaknessAttribution: '',
      plan_id_data: [],

    }
  },
  computed: {
    planItemsList() {
      return Object.keys(this.planItems).filter((k) => this.planItems[k])
    },
    groupedQuestionTypes() {
      const groups = []
      let isFirst = true
      this.materialOrder.forEach((materialName) => {
        let rows = this.questionTypes.filter((qt) => qt.material === materialName)
        if (!rows.length) return
        rows.sort((a, b) => {
          if (b.priorityScore !== a.priorityScore) return b.priorityScore - a.priorityScore
          return a.accuracy - b.accuracy
        })
        groups.push({ material: materialName, rows, isFirst })
        isFirst = false
      })
      return groups
    },
    currentWeaknesses() {
      return this.weaknessList
    },
    weaknessSummaryText() {
      if (this.weaknessSummary && this.weaknessSummary.text) {
        return this.weaknessSummary.text
      }
      return ''
    },
    attributionHintText() {
      return this.weaknessAttribution || ''
    },
    modalPerType() {
      const n = this.planItemsList.length
      return n <= 4 ? 3 : 2
    }
  },
  watch: {
    planItemsList: {
      handler(val) {
        if (val.length === 0 && !this.showGuidanceHint) {
          this.showGuidanceHint = true
          this.guidanceOpacity = 1
          this.scheduleGuidanceFade()
        }
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initSparkline()
      this.scheduleGuidanceFade()
      window.addEventListener('resize', this.handleResize)
    })
    
    this.$nextTick(() => {
      this.getOverview()
      this.getAlerts()
      this.getRadarData()
      this.getWeakness()
      this.getTopicMap()
    })

  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.sparklineChart) this.sparklineChart.dispose()
    if (this.radarChart) this.radarChart.dispose()
    if (this._guidanceTimer) clearTimeout(this._guidanceTimer)
    if (this._highlightTimer) clearTimeout(this._highlightTimer)
  },
  methods: {

      // 返回上一页
      goBack(){
        if(this.$router){
          this.$router.back();
        }else{
          window.history.back();
        }
      },

      // 学生驾驶舱- 能力图谱
      getTopicMap(){
        Http.topicMap().then((res) => {
          var data = res.data || []
          var allItems = []
          var materials = []
          data.forEach(function(group) {
            materials.push(group.material)
            group.items.forEach(function(item) {
              item.material = group.material
              allItems.push(item)
            })
          })
          this.questionTypes = allItems
          this.materialOrder = materials
        });
      },

      


      // 学生驾驶舱- 待巩固题型

      getAlerts(){
        Http.alerts().then((res) => {
            this.consolidationAlerts = res.data
        });
      },

      // 学生驾驶舱- 雷达图数据
      getRadarData(){
  
        Http.radar({period:this.activePeriod}).then((res) => {
            this.getRadarChart(res.data)
        });
      },

      getWeakness(){
        Http.weakness({tab:this.activeTab}).then((res) => {
          this.weaknessList = res.data.list || []
          this.weaknessSummary = res.data.summary || null
          this.weaknessAttribution = res.data.attribution || ''
        })
      },

      


      getRadarChart(data){  
        var chartDom = document.getElementById('radarChart');
        if (!chartDom) return;
        var myChart = echarts.init(chartDom);

        var colors = ['#00d4ff', 'rgba(255,255,255,0.5)', '#ffd700'];

        var seriesData = data.seriesData.map(function(item, index) {
          var color = colors[index] || '#00d4ff';
          if (!item.lineStyle) item.lineStyle = {};
          item.lineStyle.color = color;
          item.lineStyle.width = item.lineStyle.width || 2;
          item.symbol = 'circle';
          item.symbolSize = 6;
          return item;
        });

        var legendData = data.legendData.map(function(name, index) {
          return { name: name, itemStyle: { color: colors[index] || '#00d4ff' } };
        });

        var option = {
          tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(10,14,39,0.9)',
            borderColor: '#00d4ff',
            textStyle: { color: '#ffffff', fontSize: 12 },
            padding: [8, 12]
          },
          legend: {
            bottom: 0,
            textStyle: { color: 'rgba(240,244,255,0.8)', fontSize: 12 },
            itemWidth: 18,
            itemHeight: 3,
            itemGap: 16,
            data: legendData
          },
          radar: {
            indicator: data.indicator,
            shape: 'polygon',
            radius: '70%',
            center: ['50%', '44%'],
            splitNumber: 5,
            axisName: {
              color: '#e0e8ff',
              fontSize: 13,
              fontWeight: 500,
              padding: [4, 8]
            },
            splitLine: { lineStyle: { color: 'rgba(0,212,255,0.15)' } },
            splitArea: { areaStyle: { color: ['rgba(0,212,255,0.03)', 'rgba(0,212,255,0.06)'] } },
            axisLine: { lineStyle: { color: 'rgba(0,212,255,0.2)' } }
          },
          series: [
            {
              name: '能力图谱',
              type: 'radar',
              data: seriesData
            }
          ]
        };

        myChart.setOption(option);

        setTimeout(() => {
          myChart.resize();
        }, 100);
      },


      // 学生驾驶舱- 仪表盘总览
      getOverview(){
        Http.dashboard().then((res) => {
          this.user = res.data.user
          this.levels = res.data.levels
          this.accuracy = res.data.accuracy
          this.weekChange = res.data.weekChange
          this.ranking = res.data.ranking
          this.initWeekChart(res.data.trendData)
        });
      },

      initWeekChart(data){
        if (!data.length) return
        var chartDom = document.getElementById('weekChart');
        if (!chartDom) return;
        var myChart = echarts.init(chartDom);
        var option;

        option = {
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(0,0,0,0.8)',
            borderColor: '#00d4ff',
            borderWidth: 1,
            textStyle: { color: '#fff' },
          },
          grid: {
            left: '0%',
            right: '0%',
            top: '10%',
            bottom: '0%',
            containLabel: false
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            show: false,
            data: []
          },
          yAxis: {
            type: 'value',
            show: false,
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false }
          },
          series: [
            {
              data: data,
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: { width: 2, color: '#00d4ff' },
              itemStyle: { color: '#00d4ff' },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(0,212,255,0.5)' },
                    { offset: 1, color: 'rgba(0,212,255,0.02)' }
                  ]
                }
              }
            }
          ]
        };

        option && myChart.setOption(option);

        setTimeout(() => {
          myChart.resize();
        }, 100);

      },


    /* ===== Utility ===== */
    getAccuracyColor(acc) {
      if (acc < 50) return 'var(--state-error)'
      if (acc < 60) return 'var(--state-warning)'
      if (acc < 70) return 'var(--state-gold)'
      return 'var(--state-success)'
    },
    getStatusInfo(acc) {
      if (acc >= 80) return { label: '已掌握', cls: 'status-mastered' }
      if (acc >= 60) return { label: '需巩固', cls: 'status-consolidate' }
      if (acc >= 40) return { label: '薄弱', cls: 'status-weak' }
      return { label: '待强化', cls: 'status-critical' }
    },
    getBarColor(acc) {
      if (acc >= 80) return '#00d4ff'
      if (acc >= 60) return '#ffd700'
      return '#ff1744'
    },
    getLastPracticeLabel(days) {
      if (days === 0) return '今日'
      return days + '天前'
    },
    findQuestionTypeById(id) {
      for (let i = 0; i < this.questionTypes.length; i++) {
        if (this.questionTypes[i].id === id) return this.questionTypes[i]
      }
      return null
    },
    findQuestionTypeByFullName(fullName) {
      for (let i = 0; i < this.questionTypes.length; i++) {
        if (this.questionTypes[i].fullName === fullName) return this.questionTypes[i]
      }
      return null
    },
    extractTypeName(fullName) {
      if (!fullName) return ''
      const idx = fullName.indexOf('·')
      if (idx !== -1) return fullName.substring(idx + 1)
      return fullName
    },
    computeWeeklyChange(qt) {
      if (!qt.weeklyHistory || qt.weeklyHistory.length < 2) return 0
      const first = qt.weeklyHistory[0]
      const last = qt.weeklyHistory[qt.weeklyHistory.length - 1]
      return Math.round((last - first) * 10) / 10
    },
    renderWeeklyChange(qt) {
      const change = this.computeWeeklyChange(qt)
      if (change >= 0.5) return '↑' + change.toFixed(1) + '%'
      if (change <= -0.5) return '↓' + Math.abs(change).toFixed(1) + '%'
      return ''
    },
    weeklyChangeClass(qt) {
      const change = this.computeWeeklyChange(qt)
      if (change >= 0.5) return 'acc-change-up'
      if (change <= -0.5) return 'acc-change-down'
      return ''
    },
    renderLevelProgressIndicator(qt) {
      if (qt.material !== '阅读在线') return ''
      if (qt.targetLevel != null && qt.targetGap != null && qt.targetGap !== '' && qt.targetGap !== '0%') {
        return `<span style="color: var(--state-gold);">→ 距Lv.${qt.targetLevel}还差 ${qt.targetGap}</span>`
      }
      return `<span style="color: var(--state-success);">✓</span>`
    },
    computeDiagnosticTag(w) {
      let acc = w.accuracy
      let practiceCount = 0
      const trend = w.trend
      if (w.typeId) {
        const qt = this.findQuestionTypeById(w.typeId)
        if (qt) {
          practiceCount = qt.practiceCount
          if (typeof qt.accuracy === 'number') acc = qt.accuracy
        }
      }
      if (acc < 50) return { label: '🔴 基础薄弱', cls: 'diag-tag-red' }
      if (acc >= 50 && acc < 60 && practiceCount < 15) return { label: '🟠 熟练度不足', cls: 'diag-tag-orange' }
      if (acc >= 60 && acc < 70 && trend === 'down') return { label: '🟡 近期下滑', cls: 'diag-tag-yellow' }
      return { label: '🔵 跨级关联', cls: 'diag-tag-blue' }
    },
 
    /* ===== State helpers ===== */
    isInPlan(fullName) {
      return !!this.planItems[fullName]
    },
    isInPlanAndLocked(fullName) {
      return !!this.lockedItems[fullName]
    },
    isAlertHighlighted(a) {
      if (!this.highlightedAlertName) return false
      const alertTypeName = this.extractTypeName(a.fullName)
      return a.fullName.indexOf(this.highlightedAlertName) !== -1 || alertTypeName === this.highlightedAlertName
    },
    rowClasses(qt) {
      const isSelected = this.selectedTypeId === qt.id
      const isWeak = qt.accuracy < 60
      const isPriority = qt.priorityScore >= 60
      const isInPlan = this.isInPlan(qt.fullName)
      const isMastered = qt.accuracy >= 80
      const dimHi = this.highlightedDimRows
      return {
        'qt-row-selected': isSelected,
        'qt-row-weak': isWeak,
        'qt-row-priority': isPriority,
        'qt-row-inplan': isInPlan,
        'qt-row-mastered': isMastered,
        'dim-highlight': dimHi && this.highlightedDim === qt.dimension
      }
    },
    barCellStyle(qt, i) {
      const isMastered = qt.accuracy >= 80
      const filled = i < Math.round(qt.accuracy / 10)
      const barColor = this.getBarColor(qt.accuracy)
      const opacity = isMastered ? 0.5 : 1
      if (filled) {
        return { background: barColor, opacity }
      }
      return { opacity }
    },
    pctColor(qt) {
      const isMastered = qt.accuracy >= 80
      const isWeak = qt.accuracy < 60
      const barColor = this.getBarColor(qt.accuracy)
      if (isMastered) return 'var(--text-muted)'
      if (isWeak) return barColor
      if (qt.accuracy >= 80) return 'var(--state-success)'
      return 'var(--text-secondary)'
    },
    weaknessBorderClass(w) {
      if (w.borderType === 'red-breathe') return 'wk-border-red'
      if (w.borderType === 'orange') return 'wk-border-orange'
      if (w.borderType === 'cyan') return 'wk-border-cyan'
      return ''
    },
 
    /* ===== Interactions ===== */
    addAlertToPlan(a) {
      if (this.plan_id_data.indexOf(a.typeId) === -1) {
        this.plan_id_data.push(a.typeId)
      }
      console.log(this.plan_id_data)
      if (!this.isInPlan(a.fullName)) {
        this.$set(this.planItems, a.fullName, true)
        this.$set(this.lockedItems, a.fullName, true)
      }
    },
    togglePlanFromWeakness(e) {
      if (this.plan_id_data.indexOf(e.typeId) === -1) {
        this.plan_id_data.push(e.typeId)
      }
      console.log(this.plan_id_data)
      if (this.isInPlanAndLocked(e.fullName)) {
        this.showToast('请从底部计划栏取消')
        return
      }
      this.togglePlanItem(e.fullName)
    },
    togglePlanFromRow(e) {
      if (this.plan_id_data.indexOf(e.id) === -1) {
        this.plan_id_data.push(e.id)
      }
      if (this.isInPlanAndLocked(e.fullName)) {
        this.showToast('请从底部计划栏取消')
        return
      }
      this.togglePlanItem(e.fullName)
    },
    togglePlanItem(fullName) {
      if (this.isInPlan(fullName)) {
        this.$delete(this.planItems, fullName)
        this.$delete(this.lockedItems, fullName)
        var qt = this.findQuestionTypeByFullName(fullName)
        if (qt) {
          var id = qt.typeId !== undefined ? qt.typeId : qt.id
          var idx = this.plan_id_data.indexOf(id)
          if (idx !== -1) {
            this.plan_id_data.splice(idx, 1)
          }
        }
      } else {
        this.$set(this.planItems, fullName, true)
      }
      console.log(this.plan_id_data)
    },
    handleRowClick(qt) {
      if (this.selectedTypeId === qt.id) {
        this.selectedTypeId = null
        this.highlightedDim = -1
        this.highlightedAlertName = null
      } else {
        this.selectedTypeId = qt.id
        this.highlightedDim = qt.dimension
        this.highlightedAlertName = qt.name
        this.triggerWeaknessLinkage(qt.fullName, qt.name)
      }
    },
    triggerWeaknessLinkage(qtFullName, qtName) {
      const cats = ['absolute', 'relative', 'declining', 'leverage']
      for (let i = 0; i < cats.length; i++) {
        const arr = this.weaknesses[cats[i]] || []
        for (let j = 0; j < arr.length; j++) {
          const w = arr[j]
          if (w.fullName === qtFullName) {
            this.switchTabAndHighlight(cats[i], w.fullName)
            return
          }
          if (w.typeId) {
            const qt = this.findQuestionTypeById(w.typeId)
            if (qt && qt.fullName === qtFullName) {
              this.switchTabAndHighlight(cats[i], w.fullName)
              return
            }
          }
          if (cats[i] === 'leverage' && qtName && w.fullName === qtName) {
            this.switchTabAndHighlight(cats[i], w.fullName)
            return
          }
        }
      }
    },
    switchTabAndHighlight(tabName, wkFullName) {
      if (this.activeTab !== tabName) {
        this.activeTab = tabName
      }
      this.$nextTick(() => {
        this.highlightedWeakness = wkFullName
        if (this._highlightTimer) clearTimeout(this._highlightTimer)
        this._highlightTimer = setTimeout(() => {
          this.highlightedWeakness = null
        }, 2500)
      })
    },
    switchTab(tabName) {
      this.activeTab = tabName
      this.getWeakness()
    },
    switchPeriod(period) {
      this.activePeriod = period
      this.getRadarData()
    },
 
    /* ===== Modal ===== */
    openModal() {
      this.modalVisible = true
    },
    confirmGeneratePack() {
      this.modalVisible = false
      this.generateBtnText = '✅ 已生成'
      this.generateBtnStyle = {
        background: 'linear-gradient(135deg, var(--state-success-dim), var(--state-success))'
      }
      const items = this.planItemsList.slice()
      // clear plan
      this.planItems = {}
      this.lockedItems = {}
      setTimeout(() => {
        this.generateBtnText = '生成专属巩固包'
        this.generateBtnStyle = {}
      }, 2000)
      // accuracy bump toast
      // if (items.length) {
      //   this.showToast(`✅ 巩固包已生成，共 ${items.length * this.modalPerType} 题`)
      // }

      // 获取当前时间戳以及一周后的当前10位时间戳
      const starttime = Math.floor(Date.now() / 1000)
      const endtime = starttime + 7 * 24 * 60 * 60



      Http.mapCreate({
        mapId: this.plan_id_data,
        num: 3,
        starttime: starttime,
        endtime: endtime
      }).then(res => {
        if (res.code) {
          this.showToast('✅ 巩固包已生成,可前往学习计划中查看')
        }
      })


    },
 
    /* ===== Toast ===== */
    showToast(message) {
      const id = ++this.toastId
      this.toasts.push({ id, message })
      setTimeout(() => {
        const idx = this.toasts.findIndex((t) => t.id === id)
        if (idx !== -1) this.toasts.splice(idx, 1)
      }, 2000)
    },
 
    /* ===== Guidance hint ===== */
    scheduleGuidanceFade() {
      if (this._guidanceTimer) clearTimeout(this._guidanceTimer)
      this._guidanceTimer = setTimeout(() => {
        this.guidanceOpacity = 0
        setTimeout(() => {
          this.showGuidanceHint = false
        }, 600)
      }, 2000)
    },
 
    /* ===== Charts (ECharts) ===== */
    initSparkline() {
      const el = this.$refs.sparkline
      if (!el) return
      this.sparklineChart = echarts.init(el)
      this.sparklineChart.setOption({
        grid: { left: 0, right: 0, top: 4, bottom: 0 },
        xAxis: { type: 'category', show: false, data: [0,1,2,3,4,5,6] },
        yAxis: { type: 'value', show: false, min: 65, max: 78 },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10,14,39,0.9)',
          borderColor: 'transparent',
          textStyle: { color: '#00d4ff', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 },
          formatter: (params) => params[0].value + '%',
          padding: 6
        },
        series: [{
          type: 'line',
          data: [68, 70, 69, 72, 71, 73, 73.8],
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          hoverAnimation: true,
          lineStyle: { color: '#00d4ff', width: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0,212,255,0.25)' },
              { offset: 1, color: 'rgba(0,212,255,0.02)' }
            ])
          }
        }]
      })
    },
    handleResize() {
      if (this.sparklineChart) this.sparklineChart.resize()
    }
  }
}
</script>
 
<style>
/* ============================================================
   THEME VARIABLES
   ============================================================ */
:root {
  --color-primary-50: #e6fcff;
  --color-primary-100: #b3f5ff;
  --color-primary-200: #80edff;
  --color-primary-300: #4de5ff;
  --color-primary-400: #1addff;
  --color-primary-500: #00d4ff;
  --color-primary-600: #00a8cc;
  --color-primary-700: #007d99;
  --color-primary-800: #005366;
  --color-primary-900: #002933;
 
  --state-gold: #ffd700;
  --state-gold-dim: #b89800;
  --state-gold-bright: #ffe44d;
 
  --state-success: #00e676;
  --state-success-dim: #00a854;
  --state-warning: #ff9100;
  --state-warning-dim: #cc7400;
  --state-error: #ff1744;
  --state-error-dim: #cc1136;
  --state-info: #448aff;
 
  --bg-deep-navy: #0a0e27;
  --bg-purple-black: #12082a;
  --bg-card-surface: rgba(255, 255, 255, 0.06);
  --bg-card-hover: rgba(255, 255, 255, 0.10);
  --bg-glass-border: rgba(255, 255, 255, 0.12);
 
  --text-primary: #f0f4ff;
  --text-secondary: rgba(240, 244, 255, 0.65);
  --text-muted: rgba(240, 244, 255, 0.40);
  --text-glow-cyan: 0 0 20px rgba(0, 212, 255, 0.6), 0 0 40px rgba(0, 212, 255, 0.3);
  --text-glow-gold: 0 0 15px rgba(255, 215, 0, 0.5), 0 0 30px rgba(255, 215, 0, 0.25);
 
  --glass-blur: 16px;
  --glass-border: 1px solid var(--bg-glass-border);
  --glass-radius: 16px;
  --glass-bg: rgba(255, 255, 255, 0.06);
  --glass-bg-hover: rgba(255, 255, 255, 0.10);
 
  --grid-color: rgba(0, 212, 255, 0.04);
 
  --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', sans-serif;
  --font-mono: 'JetBrains Mono', 'SF Mono', 'Fira Code', 'Menlo', monospace;
 
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
}
</style>
 
<style scoped>
/* ============================================================
   APP SHELL & LAYOUT
   ============================================================ */
.app-shell {
  position: relative;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 16px 72px 16px;
  box-sizing: border-box;
  font-family: var(--font-sans);
  color: var(--text-primary);
}
.main-grid {
  display: grid;
  grid-template-columns: 3fr 5fr 3fr;
  gap: 16px;
  flex: 1 1 0;
  min-height: 0;
  position: relative;
  z-index: 1;
}
.col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}
.center-col { gap: 12px; }

/* Top bar (back button) */
.cockpit-topbar {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.cockpit-back-btn {
  background: rgba(0, 212, 255, 0.08) !important;
  border-color: rgba(0, 212, 255, 0.4) !important;
  color: #00d4ff !important;
}
.cockpit-back-btn:hover,
.cockpit-back-btn:focus {
  background: rgba(0, 212, 255, 0.16) !important;
  border-color: rgba(0, 212, 255, 0.7) !important;
  color: #33ddff !important;
}
.cockpit-back-btn >>> i { color: #00d4ff !important; }
.cockpit-back-btn:hover >>> i,
.cockpit-back-btn:focus >>> i { color: #33ddff !important; }

/* Background layers */
.bg-base {
  position: fixed;
  inset: 0;
  z-index: -10;
  background: linear-gradient(135deg, #0a0e27 0%, #0e0b2a 40%, #12082a 100%);
}
.bg-grid {
  position: fixed;
  inset: 0;
  z-index: -5;
  pointer-events: none;
  background-image: linear-gradient(rgba(0,212,255,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0,212,255,0.04) 1px, transparent 1px);
  background-size: 40px 40px;
}
.bg-blob {
  position: fixed;
  z-index: -5;
  pointer-events: none;
  border-radius: 50%;
}
.bg-blob-1 { top: -10%; right: -5%; width: 500px; height: 500px; background: radial-gradient(ellipse, rgba(0,212,255,0.08) 0%, transparent 70%); }
.bg-blob-2 { bottom: -10%; left: -5%; width: 600px; height: 600px; background: radial-gradient(ellipse, rgba(88,28,200,0.06) 0%, transparent 60%); }
.bg-blob-3 { top: 30%; left: 40%; width: 400px; height: 400px; background: radial-gradient(ellipse, rgba(0,212,255,0.04) 0%, transparent 70%); }
.bg-border-overlay {
  position: fixed;
  inset: 0;
  z-index: -5;
  pointer-events: none;
  border: 1px solid rgba(0,212,255,0.06);
}
 
/* ============================================================
   GLASS CARD
   ============================================================ */
.glass-card {
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border: var(--glass-border);
  border-radius: var(--glass-radius);
  padding: 16px;
}
.glass-card:hover { background: var(--glass-bg-hover); }
.glow-cyan { text-shadow: var(--text-glow-cyan); }
.glow-gold { text-shadow: var(--text-glow-gold); }
 
/* ============================================================
   CARD 1: IDENTITY
   ============================================================ */
.card-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 500;
  background: linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700));
  color: var(--text-primary);
}
.identity-info { min-width: 0; flex: 1; }
.identity-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.identity-tags {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.id-tag {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  white-space: nowrap;
  border: 1px solid;
}
.id-tag-cyan { border-color: var(--color-primary-500); color: var(--color-primary-500); box-shadow: 0 0 8px rgba(0,212,255,0.25); }
.id-tag-gold { border-color: var(--state-gold); color: var(--state-gold); box-shadow: 0 0 8px rgba(255,215,0,0.2); }
.id-level-badge {
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 13px;
  white-space: nowrap;
  background: rgba(0,230,118,0.12);
  color: var(--state-success);
  border: 1px solid rgba(0,230,118,0.25);
}
 
/* ============================================================
   CARD 2: ACCURACY
   ============================================================ */
.card-accuracy {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.acc-label { font-size: 12px; font-weight: 500; color: var(--text-secondary); text-align: left; }
.acc-main {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 4px;
}
.acc-value {
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-primary-500);
}
.acc-unit { font-size: 24px; }
.acc-change { text-align: right; }
.acc-change-val { font-size: 14px;font-weight: 500; color: var(--state-success); }
.acc-change-desc { font-size: 10px; color: var(--text-muted); }
.acc-bar-track {
  margin-top: 12px;
  height: 8px;
  border-radius: 9999px;
  overflow: hidden;
  background: rgba(255,255,255,0.08);
}
.acc-bar-fill {
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(90deg, var(--color-primary-700), var(--color-primary-500));
  box-shadow: 0 0 12px rgba(0,212,255,0.4);
}
.acc-rank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}
.rank-pill {
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-family: var(--font-mono);
  white-space: nowrap;
  background: rgba(0,230,118,0.12);
  color: var(--state-success);
}
.rank-school { font-size: 11px; font-family: var(--font-mono); color: var(--text-muted); }
.sparkline-wrap {
  min-height: 48px;
  position: relative;
  margin-top: 8px;
}
.sparkline-canvas { width: 100%; height: 48px; }
.ranking-separator { height: 1px; background: rgba(255,255,255,0.08); width: 100%; margin-top: 8px; }
.rank-detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}
.rank-trophy { color: var(--state-gold); font-size: 16px; }
.rank-detail-text { font-size: 14px; color: var(--text-secondary); }
.rank-num-gold { color: var(--state-gold); font-family: var(--font-mono); font-weight: 600; font-size: 18px;}
.rank-bar-track {
  margin-top: 8px;
  border-radius: 9999px;
  overflow: hidden;
  height: 4px;
  background: rgba(255,255,255,0.08);
}
.rank-bar-fill {
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(90deg, var(--state-gold-dim), var(--state-gold), var(--state-gold-bright));
  box-shadow: 0 0 10px rgba(255,215,0,0.4);
}
.rank-footnote { font-size: 12px; margin-top: 8px; color: var(--text-muted); text-align: left; }
 
/* ============================================================
   CARD 3: CONSOLIDATION ALERTS
   ============================================================ */
.card-alerts {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.alerts-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.alerts-title { font-size: 14px; font-weight: 500; color: var(--text-primary); }
.alerts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  flex: 1;
  min-height: 0;
}
.consolidation-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px;
  border-radius: 8px;
  background: rgba(255,23,68,0.06);
  transition: all 0.2s;
}
.consolidation-alert.alert-highlight {
  background: rgba(0,212,255,0.10) !important;
  border: 1px solid rgba(0,212,255,0.3) !important;
  box-shadow: 0 0 12px rgba(0,212,255,0.15);
  animation: alert-glow-pulse 1.5s ease-in-out infinite;
}
@keyframes alert-glow-pulse {
  0%, 100% { box-shadow: 0 0 8px rgba(0,212,255,0.15); }
  50% { box-shadow: 0 0 18px rgba(0,212,255,0.45); }
}
.alert-dot { width: 6px; height: 6px; flex-shrink: 0; }
.alert-name {
  font-size: 14px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.ca-btn {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
  font-weight: 500;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
.ca-btn-active {
  background: rgba(255,23,68,0.15);
  color: var(--state-error);
}
.ca-btn-active:hover { opacity: 0.8; transform: scale(1.05); }
.ca-btn-locked {
  background: rgba(255,255,255,0.06);
  color: var(--text-muted);
  cursor: not-allowed;
}
 
/* ============================================================
   CARD 4: RADAR
   ============================================================ */
.card-radar {
  display: flex;
  flex-direction: column;
  flex: 55;
  min-height: 0;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.card-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-icon { color: var(--color-primary-500); font-size: 16px; }
.card-title { font-size: 15px; font-weight: 500; color: var(--text-primary); }
.period-btns { display: flex; gap: 4px; }
.period-btn {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  background: transparent;
}
.period-active {
  background: rgba(0,212,255,0.2);
  color: var(--color-primary-500);
  border: 1px solid rgba(0,212,255,0.3);
}
.period-inactive {
  background: rgba(255,255,255,0.06);
  color: var(--text-secondary);
}
.period-inactive:hover { background: rgba(255,255,255,0.10); }
.radar-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
  margin-top: 8px;
}
.radar-canvas { width: 100%; height: 100%; cursor: pointer; }
 
/* ============================================================
   CARD 5: WEAKNESS
   ============================================================ */
.card-weakness {
  display: flex;
  flex-direction: column;
  flex: 45;
  min-height: 0;
}
.weakness-summary { font-size: 13px; flex-shrink: 0; color: var(--text-muted); text-align: left; }
.weakness-header { margin-top: 4px; }
.weakness-tabs { display: flex; gap: 6px; }
.weakness-tab {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  background: transparent;
}
.tab-active {
  background: rgba(0,212,255,0.2);
  color: var(--color-primary-500);
  border: 1px solid rgba(0,212,255,0.3);
}
.tab-inactive {
  background: rgba(255,255,255,0.06);
  color: var(--text-secondary);
  border: 1px solid transparent;
}
.tab-inactive:hover {
  background: rgba(255,255,255,0.10);
  color: var(--text-primary);
}
.weakness-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 12px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-top: 5px;
    box-sizing: border-box;
}
.weakness-empty {
  grid-column: 1 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  font-size: 14px;
  color: var(--text-muted);
}
.weakness-card {
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.3s;
}
.weakness-card:hover { transform: translateY(-1px); }
.wk-border-red {
  border: 1px solid rgba(255,23,68,0.5);
  background: rgba(255,23,68,0.04);
  animation: border-breathe-red 2s ease-in-out infinite;
}
.wk-border-orange {
  border: 1px solid rgba(255,145,0,0.45);
  background: rgba(255,145,0,0.04);
}
.wk-border-cyan {
  border: 1px solid rgba(0,212,255,0.45);
  background: rgba(0,212,255,0.04);
}
.wk-card-highlight {
  box-shadow: 0 0 16px 2px rgba(0,212,255,0.5) !important;
  border-color: rgba(0,212,255,0.6) !important;
}
.wk-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.wk-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  text-align: left;
}
.diag-tag {
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 9999px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.diag-tag-red { background: rgba(255,23,68,0.16); color: var(--state-error); border: 1px solid rgba(255,23,68,0.3); }
.diag-tag-orange { background: rgba(255,145,0,0.16); color: var(--state-warning); border: 1px solid rgba(255,145,0,0.3); }
.diag-tag-yellow { background: rgba(255,215,0,0.16); color: var(--state-gold); border: 1px solid rgba(255,215,0,0.3); }
.diag-tag-blue { background: rgba(68,138,255,0.16); color: var(--state-info); border: 1px solid rgba(68,138,255,0.3); }
.wk-mid-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.wk-accuracy { font-size: 24px; font-weight: 700; }
.wk-trend-icon { font-size: 14px; flex-shrink: 0; }
.wk-impact { font-size: 12px; margin-top: 2px; color: var(--text-muted); text-align: left;}
.wk-smart-wrap { margin-top: 8px; }
.wk-smart-btn {
  width: 100% !important;
  padding: 12px 0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  border: none !important;
  background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-500)) !important;
  color: white !important;
  box-shadow: 0 0 10px rgba(0,212,255,0.2) !important;
  transition: all 0.2s !important;
}
.wk-smart-btn:hover { filter: brightness(1.1); transform: scale(1.02); }
.wk-sub-actions { display: flex; gap: 12px; margin-top: 6px; }
.wk-sub-btn {
  font-size: 12px;
  white-space: nowrap;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  transition: opacity 0.2s;
  padding: 0;
}
.wk-sub-btn:hover { opacity: 0.7; }
.plan-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  padding-top: 8px;
}
.plan-toggle-label { font-size: 12px; white-space: nowrap; color: var(--text-muted); }
.plan-lock-note { font-size: 11px; color: var(--text-muted); }
.plan-checkbox {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}
.plan-checkbox.checked {
  background: var(--color-primary-500);
  border-color: var(--color-primary-500);
}
.plan-checkbox.locked { cursor: not-allowed; opacity: 0.85; }
.plan-check-icon { color: var(--bg-deep-navy); font-size: 10px; font-weight: bold; }
.attribution-hint-wrap { margin-top: 8px; flex-shrink: 0; }
.attribution-hint-pill {
  font-size: 13px;
  color: var(--text-secondary);
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 6px 10px;
  line-height: 1.4;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}
 
/* ============================================================
   CARD: QUESTION TYPES (RIGHT)
   ============================================================ */
.card-question-types {
  padding: 12px;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.qt-header { margin-bottom: 8px; }
.qt-hint { font-size: 11px; white-space: nowrap; color: var(--text-muted); }
.qt-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}
.qt-group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px 15px;
}
.qt-group-bar {
  width: 2px;
  height: 12px;
  border-radius: 9999px;
  background: var(--color-primary-500);
  flex-shrink: 0;
}
.qt-group-title { font-size: 16px; font-weight: 500; color: var(--text-primary); }
.qt-group-count { font-size: 12px; color: var(--text-muted); font-family: var(--font-mono); margin-left: auto; white-space: nowrap; }
 
.qt-row-enhanced {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, border-color 0.2s, opacity 0.2s;
  border-left: 3px solid transparent;
  background: rgba(255,255,255,0.02);
  margin-bottom: 15px;
  position: relative;
}
.qt-row-enhanced:hover { background: rgba(255,255,255,0.07); z-index: 10; }
.qt-row-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1 1 auto;
}
.qt-row-line1 { display: flex; align-items: center; gap: 10px; min-width: 0; }
.qt-row-line2 { display: flex; align-items: center; gap: 10px; min-width: 0; }
.qt-row-line3 {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 10px;
  line-height: 1.3;
}
.qt-name {
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qt-status-pill {
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 9999px;
  white-space: nowrap;
  font-family: var(--font-mono);
  flex-shrink: 0;
}
.status-mastered { background: rgba(0,230,118,0.16); color: var(--state-success); border: 1px solid rgba(0,230,118,0.3); }
.status-consolidate { background: rgba(255,215,0,0.16); color: var(--state-gold); border: 1px solid rgba(255,215,0,0.3); }
.status-weak { background: rgba(255,145,0,0.16); color: var(--state-warning); border: 1px solid rgba(255,145,0,0.3); }
.status-critical { background: rgba(255,23,68,0.16); color: var(--state-error); border: 1px solid rgba(255,23,68,0.3); }
.priority-fire {
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px rgba(255,145,0,0.5));
  cursor: help;
  position: relative;
}
.fire-tooltip-content { font-size: 12px; line-height: 1.5; }
.qt-bar-10 { display: flex; gap: 1px; width: 70px; height: 6px; flex-shrink: 0; }
.qt-bar-cell {
  flex: 1 1 0;
  background: rgba(255,255,255,0.08);
  border-radius: 1px;
  transition: background 0.3s;
}
.qt-bar-cell.filled { border-radius: 1px; }
.qt-pct { font-size: 13px; min-width: 30px; text-align: right; font-family: var(--font-mono); }
.acc-change { font-size: 12px; font-family: var(--font-mono); white-space: nowrap; }
.acc-change-up { color: var(--state-success); }
.acc-change-down { color: var(--state-error); }
.lv-tag {
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 6px;
  white-space: nowrap;
  font-family: var(--font-mono);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.lv-tag.lv-1 { background: rgba(255,23,68,0.15); color: #ff1744; }
.lv-tag.lv-2 { background: rgba(255,145,0,0.15); color: #ff9100; }
.lv-tag.lv-3 { background: rgba(255,215,0,0.15); color: #ffd700; }
.lv-tag.lv-4 { background: rgba(0,212,255,0.15); color: #00d4ff; }
.lv-tag.lv-5 { background: rgba(0,230,118,0.15); color: #00e676; }
.lv-tag.lv-6 { background: rgba(255,215,0,0.25); color: #ffe44d; }
.lv-tag.lv-7 { background: rgba(68,138,255,0.18); color: #448aff; }
.qt-meta { font-size: 11px; white-space: nowrap; color: var(--text-muted); }
.qt-meta-sep { font-size: 11px; opacity: 0.4; color: var(--text-muted); }
.lv-progress-indicator { font-size: 10px; white-space: nowrap; font-family: var(--font-mono); }
.qt-row-weak {
  border-left: 3px solid var(--state-error);
  box-shadow: inset 0 0 14px rgba(255,23,68,0.05), 0 0 6px rgba(255,23,68,0.04);
}
.qt-row-priority {
  background: linear-gradient(90deg, rgba(255,215,0,0.05) 0%, rgba(255,215,0,0.02) 60%, transparent 100%);
}
.qt-row-priority::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: linear-gradient(110deg, transparent 40%, rgba(255,215,0,0.06) 50%, transparent 60%);
  background-size: 200% 100%;
  animation: gold-row-shimmer 3s linear infinite;
  pointer-events: none;
}
.qt-row-selected {
  background: rgba(0,212,255,0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(0,212,255,0.4);
}
.qt-row-inplan { box-shadow: inset 0 0 14px rgba(0,230,118,0.06); }
.qt-row-mastered { opacity: 0.55; }
.qt-row-mastered:hover { opacity: 0.85; }
.qt-row-enhanced.dim-highlight {
  background: rgba(0,212,255,0.16) !important;
  box-shadow: inset 0 0 0 1px rgba(0,212,255,0.35);
}
.qt-plan-check {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s;
}
.qt-plan-check.checked {
  background: var(--color-primary-500);
  border-color: var(--color-primary-500);
}
.qt-plan-check.locked { cursor: not-allowed; opacity: 0.85; }
.qt-check-icon { color: var(--bg-deep-navy); font-size: 10px; font-weight: bold; }
 
/* ============================================================
   BOTTOM BAR
   ============================================================ */
.bottom-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  height: 56px;
  z-index: 50;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 16px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  background: rgba(10,14,39,0.88);
  border-top: 1px solid var(--bg-glass-border);
  box-sizing: border-box;
}
.bottom-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.bottom-title { font-size: 14px; font-weight: 500; white-space: nowrap; color: var(--text-primary); }
.plan-items {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}
.plan-items::-webkit-scrollbar { display: none; }
.plan-items { -ms-overflow-style: none; scrollbar-width: none; }
.plan-item-label {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}
.plan-item-label:hover { opacity: 0.75; }
.plan-checkbox-bottom {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--color-primary-500);
  border: 1px solid var(--color-primary-500);
}
.plan-item-text { font-size: 12px; white-space: nowrap; color: var(--text-secondary); }
.plan-count { font-size: 12px; font-family: var(--font-mono); flex-shrink: 0; white-space: nowrap; color: var(--text-muted); }
.plan-guidance-hint {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
  white-space: nowrap;
  transition: opacity 0.6s;
}
.btn-generate-pack {
  padding: 8px 16px !important;
  border-radius: 9999px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  flex-shrink: 0;
  border: none !important;
  background: linear-gradient(135deg, var(--color-primary-700), var(--color-primary-500)) !important;
  color: white !important;
  box-shadow: 0 0 20px rgba(0,212,255,0.3) !important;
  transition: all 0.2s !important;
}
.btn-generate-pack:hover { box-shadow: 0 0 28px rgba(0,212,255,0.5) !important; transform: scale(1.05); }
 
/* ============================================================
   TOAST
   ============================================================ */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}
.toast-item {
  background: rgba(20,14,50,0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(0,212,255,0.25);
  border-radius: 10px;
  padding: 8px 14px;
  color: var(--text-primary);
  font-size: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 12px rgba(0,212,255,0.1);
  animation: toast-slide-in 0.25s ease-out;
  white-space: nowrap;
  pointer-events: auto;
}
 
/* ============================================================
   SCROLLBAR
   ============================================================ */
.custom-scroll::-webkit-scrollbar { width: 4px; }
.custom-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); border-radius: 4px; }
.custom-scroll::-webkit-scrollbar-thumb { background: rgba(0,212,255,0.3); border-radius: 4px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,212,255,0.5); }
 
/* ============================================================
   ANIMATIONS
   ============================================================ */
@keyframes pulse-red {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 23, 68, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(255, 23, 68, 0); }
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}
.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: pulse-dot 1.5s ease-in-out infinite;
}
.pulse-dot-red { background: var(--state-error); }
 
@keyframes border-breathe-red {
  0%, 100% { border-color: rgba(255, 23, 68, 0.4); }
  50% { border-color: rgba(255, 23, 68, 0.8); }
}
 
@keyframes gold-row-shimmer {
  0% { background-position: -150% 0; }
  100% { background-position: 150% 0; }
}
 
@keyframes toast-slide-in {
  from { opacity: 0; transform: translate(-50%, -20px); }
  to { opacity: 1; transform: translate(0, 0); }
}
 
.mt-1 { margin-top: 4px; }
.mt-2 { margin-top: 8px; }
 
/* Responsive: smaller screens compress */
@media (max-width: 1280px) {
  .main-grid { grid-template-columns: 3fr 5fr 3fr; gap: 12px; }
  .acc-value { font-size: 40px; }
}
@media (max-width: 1024px) {
  .main-grid { grid-template-columns: 2.6fr 4.4fr 3fr; gap: 10px; }
  .app-shell { padding: 12px 12px 72px 12px; }
  .acc-value { font-size: 36px; }
  .weakness-grid { 
    grid-template-columns: 1fr 1fr; 
    gap: 15px; 
    padding-top: 5px;
    box-sizing: border-box;
}
}
@media (max-width: 900px) {
  .main-grid { grid-template-columns: 1fr 1fr; grid-template-rows: auto auto; }
  .card-question-types { grid-column: 1 / 3; max-height: 360px; }
}
</style>
 
<style>
/* ============================================================
   ELEMENT UI OVERRIDES (global, unscoped for dialog)
   ============================================================ */
.pack-dialog {
  background: rgba(20,14,50,0.95) !important;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(0,212,255,0.2) !important;
  border-radius: 16px !important;
  overflow: hidden;
}
.pack-dialog .el-dialog__header {
  padding: 20px 24px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.pack-dialog .el-dialog__body {
  padding: 16px 24px;
  color: #f0f4ff;
}
.pack-dialog .el-dialog__footer {
  padding: 12px 24px 20px;
}
.pack-dialog .dialog-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #f0f4ff;
}
.pack-dialog .dialog-title-icon { color: #00d4ff; font-size: 18px; }
.pack-dialog .modal-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 8px;
}
.pack-dialog .modal-items::-webkit-scrollbar { width: 4px; }
.pack-dialog .modal-items::-webkit-scrollbar-thumb { background: rgba(0,212,255,0.3); border-radius: 4px; }
.pack-dialog .modal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0,212,255,0.06);
  border: 1px solid rgba(0,212,255,0.15);
}
.pack-dialog .modal-item-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.pack-dialog .modal-check-icon { color: #00d4ff; font-size: 16px; flex-shrink: 0; }
.pack-dialog .modal-item-name { font-size: 13px; color: #f0f4ff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pack-dialog .modal-item-count { font-size: 12px; font-family: 'JetBrains Mono', monospace; color: rgba(240,244,255,0.65); flex-shrink: 0; }
.pack-dialog .modal-empty { font-size: 14px; text-align: center; padding: 16px 0; color: rgba(240,244,255,0.4); }
.pack-dialog .modal-total { font-size: 12px; color: rgba(240,244,255,0.65); margin-bottom: 8px; }
.pack-dialog .dialog-footer { display: flex; gap: 12px; justify-content: flex-end; }
.pack-dialog .dialog-cancel-btn {
  padding: 8px 16px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  background: rgba(255,255,255,0.08) !important;
  color: rgba(240,244,255,0.65) !important;
  border: 1px solid transparent !important;
}
.pack-dialog .dialog-cancel-btn:hover { background: rgba(255,255,255,0.12) !important; }
.pack-dialog .dialog-confirm-btn {
  padding: 8px 16px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: linear-gradient(135deg, #007d99, #00d4ff) !important;
  color: white !important;
  border: none !important;
  box-shadow: 0 0 12px rgba(0,212,255,0.25) !important;
}
.pack-dialog .dialog-confirm-btn:hover { filter: brightness(1.1); }
 
/* el-tooltip dark theme override for fire tooltip */
.el-tooltip__popper.is-dark {
  background: rgba(20,14,50,0.96) !important;
  color: #f0f4ff !important;
  border: 1px solid rgba(255,215,0,0.35) !important;
  font-size: 11px;
  max-width: 200px;
}
.el-tooltip__popper.is-dark .popper__arrow {
  background: rgba(20,14,50,0.96) !important;
}
</style>